// Client-safe half of the loop builder — the contract types + UI constants that
// both the server proposal engine (builder.ts) AND the client wizard
// (app/agents/workflows/new/_builder.tsx) need.
//
// Split out of builder.ts because that module imports `listRoles` from
// @excellent/agents (a `node:fs` filesystem read of packages/agents/roles/),
// which cannot be bundled into the browser. A client importing a runtime value
// from builder.ts therefore drags node:fs into the client graph and breaks
// `next build`. Everything here is pure types + plain constants; `import type`
// of a core type is fully erased, so it's safe on both sides of the boundary.
// builder.ts re-exports all of this, so its server importers are unchanged.

import type { LoopStepTemplate } from "@excellent/core";

export type ObjectivePreset = "cost" | "balanced" | "quality";
export type GatePolicy = "ship-verify" | "human" | "auto-eval";

export interface LoopProposal {
  title: string;
  domain: string;
  description: string;
  steps: LoopStepTemplate[];
  objectivePreset: ObjectivePreset;
  objective: Record<string, unknown>;
  gate_policy: GatePolicy;
  rationale: string;
  /** "preset" = deterministic only; "llm-refined" = a claude pass adjusted it. */
  source: "preset" | "llm-refined";
}

// ── Objective presets ───────────────────────────────────────────────────────
// Every loop optimizes a weighted/constrained mix (PRD §4.3). These three cover
// the common intents; the builder picks one and the user can switch via chips.
export const OBJECTIVE_PRESETS: Record<
  ObjectivePreset,
  { label: string; description: string; objective: Record<string, unknown> }
> = {
  cost: {
    label: "Cheapest that passes",
    description: "Hold correctness, drive cost + tokens down hard.",
    objective: {
      correctness: { weight: 1, floor: 0.9 },
      cost_usd: { weight: 0.8, minimize: true },
      tokens: { weight: 0.5, minimize: true },
    },
  },
  balanced: {
    label: "Balanced",
    description: "Keep correctness high, trim cost where it's free.",
    objective: {
      correctness: { weight: 1, floor: 0.95 },
      cost_usd: { weight: 0.5, minimize: true },
      tokens: { weight: 0.3, minimize: true },
    },
  },
  quality: {
    label: "Quality first",
    description: "Correctness floor at 1.0; cost is a tie-breaker only.",
    objective: {
      correctness: { weight: 1, floor: 1.0 },
      cost_usd: { weight: 0.2, minimize: true },
    },
  },
};

export const GATE_POLICY_COPY: Record<GatePolicy, string> = {
  "ship-verify": "a different agent verifies each promotion",
  human: "every output is human-gated before it acts",
  "auto-eval": "low-risk prompt-only wins auto-promote after re-eval",
};
